"use client"

import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "next/navigation"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { motion, AnimatePresence } from "framer-motion"
import { Eye, EyeOff, Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"
import { Icon } from "@/shared/components/icons"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"

import { loginSchema, type LoginInput } from "@/features/auth/validations"
import { signIn } from "@/features/auth/actions"
import { isSafeNextPath } from "@/features/auth/lib/parse-auth-callback-url"
import { AuthCharacterPanel } from "@/features/auth/components/auth-character-panel"
import { EmailVerificationResend } from "@/features/auth/components/email-verification-resend"
import { track } from "@/shared/lib/analytics"
import { DELETED_ACCOUNT_MESSAGE } from "@/features/auth/lib/signup-error-message"
import {
  LOGIN_ERROR_MESSAGE_KEY,
  runPasswordLogin,
} from "@/features/auth/lib/login-error"
import { useLoginRateLimit } from "@/features/auth/hooks/use-login-rate-limit"
import { createClient } from "@/shared/lib/supabase/client"
import { ROUTES, getAuthCallbackUrl } from "@/shared/lib/constants"
import { cn } from "@/shared/lib/utils"
import { useCharacter } from "@/shared/hooks/use-character"
import { useLocalizedRouter } from "@/shared/hooks/use-localized-router"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card"
import { Separator } from "@/shared/components/ui/separator"

export function LoginContent() {
  const t = useTranslations("auth.login")
  const auth = useTranslations("auth.shared")
  const forms = useTranslations("forms")
  const navigation = useTranslations("chrome.nav")
  const router = useLocalizedRouter()
  const searchParams = useSearchParams()
  const characterId = searchParams.get("characterId") ?? ""
  const { data: character } = useCharacter(characterId)
  const showCharacter = !!character?.avatar
  const redirectParam = searchParams.get("redirect")
  const safeRedirect = redirectParam && isSafeNextPath(redirectParam) ? redirectParam : null
  const registerParams = new URLSearchParams()
  if (characterId) registerParams.set("characterId", characterId)
  if (safeRedirect) registerParams.set("redirect", safeRedirect)
  const registerQuery = registerParams.toString()
  const registerHref = registerQuery ? `${ROUTES.register}?${registerQuery}` : ROUTES.register
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [showUnverifiedView, setShowUnverifiedView] = useState(false)
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null)
  const hasTrackedEmailFocus = useRef(false)
  const rateLimit = useLoginRateLimit()

  useEffect(() => {
    track("auth:login_view", {})
  }, [])

  function handleEmailFocus() {
    if (hasTrackedEmailFocus.current) return

    hasTrackedEmailFocus.current = true
    track("auth:login_field_focused", { field: "email" })
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    defaultValues: {
      email: "",
      password: "",
    },
  })

  async function onSubmit(data: LoginInput) {
    if (rateLimit.isLockedOut) {
      setServerError(
        t("tooManyAttempts", {
          minutes: Math.ceil(rateLimit.lockoutRemainingSeconds / 60),
        })
      )
      return
    }

    setIsLoading(true)
    setServerError(null)

    const outcome = await runPasswordLogin({
      preCheck: () => signIn({ email: data.email, password: data.password }),
      signInWithPassword: () =>
        createClient().auth.signInWithPassword({
          email: data.email,
          password: data.password,
        }),
    })

    if (outcome.type === "success") {
      rateLimit.reset()
      const redirectTo = searchParams.get("redirect") || "/"
      router.push(redirectTo)
      router.refresh()
      return
    }

    if (outcome.type === "precheck_failed") {
      track("auth:login_fail", { method: "email", reason: outcome.message })
      setServerError(outcome.message)
      rateLimit.recordFailure()
    } else if (outcome.type === "unknown_error") {
      track("auth:login_fail", { method: "email", reason: "unknown_error" })
      setServerError(t("unknownError"))
    } else if (outcome.kind === "email_not_confirmed") {
      setUnverifiedEmail(data.email)
      setShowUnverifiedView(true)
    } else {
      track("auth:login_fail", {
        method: "email",
        reason: outcome.kind === "network" ? "auth_service_unavailable" : outcome.kind,
      })
      if (outcome.kind === "invalid_credentials") rateLimit.recordFailure()
      setServerError(t(LOGIN_ERROR_MESSAGE_KEY[outcome.kind]))
    }
    setIsLoading(false)
  }

  async function handleGoogleSignIn() {
    setIsLoading(true)
    setServerError(null)
    const supabase = createClient()
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        // intent=login so the /api/auth/callback route can route any
        // post-exchange failure to auth:login_fail (vs auth:signup_fail),
        // keeping the signup-abandonment funnel free of returning-user noise.
        redirectTo: (() => {
          const callbackParams = new URLSearchParams({ intent: "login" })
          if (safeRedirect) callbackParams.set("next", safeRedirect)
          return `${getAuthCallbackUrl()}?${callbackParams.toString()}`
        })(),
      },
    })
    if (error) {
      setServerError(error.message)
      setIsLoading(false)
    }
  }

  return (
    <div
      className={cn(
        "mx-auto w-full",
        showCharacter
          ? "grid max-w-4xl overflow-hidden rounded-2xl border border-border/50 sm:grid-cols-[0.95fr_1fr]"
          : "max-w-[335px] sm:max-w-[390px]"
      )}
    >
      <AuthCharacterPanel
        characterAvatar={character?.avatar}
        characterName={character?.name}
        className="sm:border-r sm:border-white/[0.08]"
      />
      <Card className="overflow-hidden rounded-[1.35rem] border-white/[0.12] bg-[radial-gradient(circle_at_30%_0%,rgba(29,125,242,0.18),transparent_36%),linear-gradient(145deg,#171218_0%,#0d1118_54%,#09090c_100%)] shadow-[0_24px_90px_rgba(0,0,0,0.52),inset_0_1px_0_rgba(255,255,255,0.08)] sm:rounded-[1.55rem]">
        <AnimatePresence mode="wait">
          {!showUnverifiedView ? (
            <motion.div
              key="login-form"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <CardHeader className="px-4 pt-5 text-center sm:px-8 sm:pt-8">
                <CardTitle className="text-xl font-semibold text-white sm:text-[22px]">{t("title")}</CardTitle>
                <CardDescription className="mt-1 text-sm text-white/60">{t("subtitle")}</CardDescription>
              </CardHeader>

              <CardContent className="px-4 pb-2 sm:px-8">
                {searchParams.get("password_reset") === "success" && (
                  <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
                    <p className="text-sm text-emerald-600 dark:text-emerald-400">
                      {t("passwordUpdated")}
                    </p>
                  </div>
                )}

                {searchParams.get("error") === "session_expired" && (
                  <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                    <p className="text-sm text-destructive">
                      {t("resetExpired")}
                    </p>
                  </div>
                )}

                {serverError && (
                  <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                    <p className="text-sm text-destructive">{serverError}</p>
                    {serverError === DELETED_ACCOUNT_MESSAGE && (
                      <LocalizedLink
                        href={ROUTES.support}
                        className="mt-2 block text-sm font-medium text-foreground underline underline-offset-4 transition-colors hover:text-foreground/80"
                      >
                        {navigation("support")}
                      </LocalizedLink>
                    )}
                    {rateLimit.shouldSuggestForgotPassword && (
                      <LocalizedLink
                        href={ROUTES.forgotPassword}
                        className="mt-1 block text-sm font-medium text-foreground hover:text-foreground/70 transition-colors"
                      >
                        {t("resetHere")}
                      </LocalizedLink>
                    )}
                  </div>
                )}

                {searchParams.get("error") === "deleted_account_blocked" && (
                  <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                    <p className="text-sm text-destructive">
                      {t("deletedBlocked")}
                    </p>
                    <LocalizedLink
                      href={ROUTES.support}
                      className="mt-2 block text-sm font-medium text-foreground underline underline-offset-4 transition-colors hover:text-foreground/80"
                    >
                      {navigation("support")}
                    </LocalizedLink>
                  </div>
                )}

                {searchParams.get("error") === "auth_callback_error" && (
                  <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                    <p className="text-sm text-destructive">
                      {t("authFailed")}{" "}
                      {searchParams.get("message")
                        ? decodeURIComponent(searchParams.get("message")!)
                        : t("unknownError")}
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-[13px] text-white/80">{auth("email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={forms("emailPlaceholder")}
                      autoComplete="email"
                      disabled={isLoading}
                      {...register("email")}
                      onFocus={handleEmailFocus}
                      className={cn(
                        "h-10 rounded-xl border-white/[0.08] bg-white/[0.04] text-white placeholder:text-white/40 focus-visible:border-white/20 focus-visible:ring-white/30 sm:h-11",
                        errors.email && "border-red-500/50 focus-visible:ring-red-500/30"
                      )}
                    />
                    {errors.email && (
                      <p className="text-xs text-destructive">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-[13px] text-white/80">{auth("password")}</Label>
                      <LocalizedLink
                        href={ROUTES.forgotPassword}
                        className="text-xs text-foreground hover:text-foreground/80 transition-colors"
                      >
                        {auth("forgotPassword")}
                      </LocalizedLink>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={forms("passwordPlaceholder")}
                        autoComplete="current-password"
                        disabled={isLoading}
                        {...register("password")}
                        className={cn(
                          "h-10 rounded-xl border-white/[0.08] bg-white/[0.04] pr-10 text-white placeholder:text-white/40 focus-visible:border-white/20 focus-visible:ring-white/30 sm:h-11",
                          errors.password && "border-red-500/50 focus-visible:ring-red-500/30"
                        )}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-xs text-destructive">{errors.password.message}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading || rateLimit.isLockedOut}
                    className="h-11 w-full rounded-xl bg-white text-sm font-medium text-black transition-all hover:bg-white/90 active:scale-[0.98] sm:h-12 sm:text-[15px]"
                    size="lg"
                  >
                    {rateLimit.isLockedOut ? (
                      t("locked", {
                        minutes: Math.ceil(rateLimit.lockoutRemainingSeconds / 60),
                        seconds: rateLimit.lockoutRemainingSeconds % 60,
                      })
                    ) : isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t("signingIn")}
                      </>
                    ) : (
                      auth("signIn")
                    )}
                  </Button>
                </form>

                <div className="relative my-5">
                  <Separator className="bg-white/[0.08]" />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#10131b] px-3 text-xs text-white/40">
                    {auth("orContinueWith")}
                  </span>
                </div>

                <Button
                  variant="outline"
                  type="button"
                  disabled={isLoading}
                  onClick={handleGoogleSignIn}
                  className="h-11 w-full rounded-xl border-white/[0.08] bg-white text-sm font-medium text-[#1f1f1f] transition-all hover:bg-white/90 active:scale-[0.98] sm:h-12 sm:text-[15px]"
                >
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  {auth("continueWithGoogle")}
                </Button>
              </CardContent>

              <CardFooter className="justify-center px-4 pb-5 pt-2 sm:px-8 sm:pb-7">
                <p className="text-sm text-white/60">
                  {t("noAccount")}{" "}
                  <LocalizedLink
                    href={registerHref}
                    className="font-medium text-foreground hover:text-foreground/80 transition-colors"
                  >
                    {auth("signUp")}
                  </LocalizedLink>
                </p>
              </CardFooter>
            </motion.div>
          ) : (
            <motion.div
              key="unverified"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/10">
                  <Icon name="message" size={32} className="h-8 w-8 text-foreground" />
                </div>
                <CardTitle className="text-2xl font-bold">{t("verifyTitle")}</CardTitle>
                <CardDescription className="text-left">
                  {t("verifyDescription", { email: unverifiedEmail ?? "" })}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <EmailVerificationResend
                  email={unverifiedEmail ?? ""}
                  surface="page"
                  context="login_unverified"
                  onConfirmed={() => {
                    const redirectParam = searchParams.get("redirect")
                    const target =
                      redirectParam?.startsWith("/") &&
                      !redirectParam.startsWith("//")
                        ? redirectParam
                        : "/"
                    window.location.assign(target)
                  }}
                />
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowUnverifiedView(false)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {auth("backToSignIn")}
                  </button>
                </div>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </div>
  )
}
